import { useMemo, useState } from "react";
import {
  calculateScenarios,
  visibleMortgageScenarios,
  parsePolishNumber,
  validateMortgageInput,
} from "./calculations";
import {
  Actions,
  Assumptions,
  Availability,
  Badge,
  CTA,
  Details,
  Disclaimer,
  Error,
  Eyebrow,
  Facts,
  Field,
  FieldGrid,
  Form,
  Funnel,
  Hero,
  Hint,
  Input,
  Insight,
  Kicker,
  Layout,
  Lead,
  Note,
  Page,
  Panel,
  Payment,
  ResultHeading,
  Scenario,
  ScenarioGrid,
  Secondary,
  Select,
  Submit,
  Table,
  TableWrap,
  Title,
} from "./styled";
import { pushDataLayer } from "../../analytics";

const money = new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 0 });
const preciseMoney = new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN", minimumFractionDigits: 2 });
const percent = (value) => `${Number(value).toFixed(2).replace(".", ",")} %`;

const initialValues = {
  price: "600000",
  savings: "120000",
  years: "30",
  repaymentType: "equal",
  rateType: "variable",
  calculationMode: "realistic",
  singleRate: "6,00",
  rate10: "6,40",
  rate20: "5,70",
  rateRkm: "6,00",
};

const ScenarioSchedule = ({ rows, repaymentType }) => (
  <Details>
    <summary>Harmonogram: pierwsze 12 rat</summary>
    <TableWrap>
      <Table>
        <caption>Raty {repaymentType === "decreasing" ? "malejące" : "równe"}</caption>
        <thead><tr><th>Rata</th><th>Łącznie</th><th>Kapitał</th><th>Odsetki</th><th>Saldo</th></tr></thead>
        <tbody>{rows.slice(0, 12).map((row) => (
          <tr key={row.month}>
            <td>{row.month}</td>
            <td>{preciseMoney.format(row.installment)}</td>
            <td>{preciseMoney.format(row.capital)}</td>
            <td>{preciseMoney.format(row.interest)}</td>
            <td>{preciseMoney.format(row.balance)}</td>
          </tr>
        ))}</tbody>
      </Table>
    </TableWrap>
  </Details>
);

const ScenarioCard = ({ item, best, repaymentType }) => {
  const blockedRkm = item.key === "rkm" && !item.eligible;

  return (
    <Scenario $best={item.key === best} $rkm={item.key === "rkm"} $blocked={blockedRkm}>
      {item.key === best && <Badge>najbliżej Twoich środków</Badge>}
      {blockedRkm && <Availability>wariant RKM niedostępny dla podanych danych</Availability>}
      <Kicker>{item.kicker}</Kicker>
      <h3>{item.title}</h3>

      {blockedRkm ? (
        <>
          <p className="blocked-title">Nie pokazujemy raty dla wariantu, który nie spełnia podstawowych limitów programu.</p>
          <Facts>
            <div><dt>Wkład potrzebny przy gwarancji</dt><dd>{money.format(item.own)}</dd></div>
            <div><dt>Maksymalna gwarancja BGK</dt><dd>{money.format(100000)}</dd></div>
            {item.missing > 0 && <div><dt>Minimalnie dołóż z własnych środków</dt><dd>{money.format(item.missing)}</dd></div>}
          </Facts>
          <Availability as="div">{item.eligibilityReasons.map((reason) => <span key={reason}>{reason}</span>)}</Availability>
          <Note>Po zmianie danych kliknij ponownie „Policz warianty”. RKM może być dostępny również bez gwarancji BGK. Limit wkładu pieniężnego wynosi 20% przy stopie zmiennej albo 30% przy stopie stałej przez co najmniej 5 lat, maksymalnie 200 000 zł.</Note>
        </>
      ) : (
        <>
          <Payment>{money.format(item.first)} <small>/ {repaymentType === "decreasing" ? "pierwsza rata" : "rata miesięczna"}</small></Payment>
          <Facts>
            <div><dt>Oprocentowanie</dt><dd>{percent(item.rate)}</dd></div>
            <div><dt>{item.key === "rkm" ? "Wkład gotówkowy" : "Docelowy wkład"}</dt><dd>{money.format(item.own)}</dd></div>
            <div><dt>Kwota kredytu</dt><dd>{money.format(item.loan)}</dd></div>
            {item.key === "rkm" && <div><dt>Gwarancja BGK</dt><dd>{money.format(item.guarantee)}</dd></div>}
            {item.key === "rkm" && <div><dt>Prowizja BGK 1%</dt><dd>{money.format(item.guaranteeFee)}</dd></div>}
            {item.key === "rkm" && <div><dt>Limit wkładu w wybranym wariancie</dt><dd>{item.maxOwnPercent}% ceny</dd></div>}
            {repaymentType === "decreasing" && <div><dt>Ostatnia rata</dt><dd>{money.format(item.last)}</dd></div>}
            {item.missing > 0 && <div><dt>Brakuje do wariantu</dt><dd>{money.format(item.missing)}</dd></div>}
            <div><dt>Odsetki w symulacji</dt><dd>{money.format(item.interest)}</dd></div>
          </Facts>
          <Note>{item.key === "rkm"
            ? (item.guarantee > 0
              ? `BGK: gwarancja maksymalnie 100 000 zł. Wkład i gwarancja łącznie uzupełniają 20% ceny, do limitu 200 000 zł. Gwarancja jest częścią kredytu, a nie dodatkową gotówką.`
              : `Wkład wynosi co najmniej 20% ceny, dlatego gwarancja BGK nie jest potrzebna. RKM może być dostępny bez gwarancji: limit wkładu to ${item.maxOwnPercent}% ceny, maksymalnie 200 000 zł.${item.maxOwnPercent === 30 ? " Stała stopa musi obowiązywać przez co najmniej 5 lat." : ""} Bank sprawdza pozostałe warunki programu.`)
            : "Nie obejmuje kosztów zakupu, prowizji, ubezpieczeń ani indywidualnych warunków banku."}</Note>
          <ScenarioSchedule rows={item.rows} repaymentType={repaymentType} />
        </>
      )}
    </Scenario>
  );
};

const KalkulatorRataWklad = () => {
  const [values, setValues] = useState(initialValues);
  const [submitted, setSubmitted] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [copied, setCopied] = useState(false);

  const input = useMemo(() => Object.fromEntries(Object.entries(submitted).map(([key, value]) => [
    key,
    ["repaymentType", "rateType", "calculationMode"].includes(key) ? value : parsePolishNumber(value),
  ])), [submitted]);
  const result = useMemo(() => calculateScenarios(input), [input]);
  const visibleItems = visibleMortgageScenarios(result.items, input.price, input.savings);
  const isDirty = Object.keys(values).some((key) => values[key] !== submitted[key]);

  const update = (event) => setValues((current) => ({ ...current, [event.target.name]: event.target.value }));
  const handleSubmit = (event) => {
    event.preventDefault();
    const parsed = Object.fromEntries(Object.entries(values).map(([key, value]) => [
      key,
      ["repaymentType", "rateType", "calculationMode"].includes(key) ? value : parsePolishNumber(value),
    ]));
    const nextErrors = validateMortgageInput(parsed);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      setSubmitted(values);
      pushDataLayer("sf_mortgage_calculator_submit", {
        repayment_type: parsed.repaymentType,
        rate_type: parsed.rateType,
        calculation_mode: parsed.calculationMode,
      });
      window.requestAnimationFrame(() => document.getElementById("wynik-raty")?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  };

  const copySummary = async () => {
    const scenarioLines = visibleItems.map((item) => [
      `${item.title}: kredyt ${money.format(item.loan)}, ${input.repaymentType === "decreasing" ? "pierwsza rata" : "rata"} ${preciseMoney.format(item.first)}, oprocentowanie ${percent(item.rate)}`,
      item.missing > 0 ? `brakuje do wariantu ${money.format(item.missing)}` : "",
      item.key === "rkm" ? `gwarancja BGK ${money.format(item.guarantee)}, prowizja BGK ${money.format(item.guaranteeFee)}` : "",
    ].filter(Boolean).join(", "));
    const text = [
      "Wynik kalkulatora Serce Finansów",
      `Cena nieruchomości: ${money.format(input.price)}`,
      `Posiadane środki: ${money.format(input.savings)}`,
      `Okres: ${input.years} lat`,
      `Rodzaj rat: ${input.repaymentType === "decreasing" ? "malejące" : "równe"}`,
      input.calculationMode === "single"
        ? `Oprocentowanie do symulacji: ${percent(input.singleRate)}`
        : `Oprocentowanie wariantów: ${visibleItems.map(item => `${item.title} ${percent(item.rate)}`).join(", ")}`,
      ...scenarioLines,
      "Wynik orientacyjny, nie stanowi oferty banku ani decyzji kredytowej.",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      pushDataLayer("sf_mortgage_calculator_copy");
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <Page>
      <Hero>
        <Eyebrow>Bezpłatne narzędzie</Eyebrow>
        <Title>Rata i wkład własny</Title>
        <Lead>Sprawdź, jak zmienia się kwota kredytu, rata i suma odsetek przy wkładzie 10%, 20% oraz w wariancie RKM.</Lead>
      </Hero>

      <Layout>
        <Panel>
          <Form onSubmit={handleSubmit} noValidate>
            <h2>Twoja sytuacja</h2>
            <Hint>Wynik jest symulacją edukacyjną. Nie jest ofertą banku ani decyzją kredytową.</Hint>
            <Field>Cena nieruchomości<Input aria-label="Cena nieruchomości" name="price" value={values.price} onChange={update} inputMode="decimal" $invalid={errors.price} />{errors.price && <Error>{errors.price}</Error>}</Field>
            <Field>Posiadane środki<Input aria-label="Posiadane środki" name="savings" value={values.savings} onChange={update} inputMode="decimal" $invalid={errors.savings} /><Hint>Nie zapomnij o kosztach zakupu i rezerwie bezpieczeństwa.</Hint>{errors.savings && <Error>{errors.savings}</Error>}</Field>
            <Field>Okres kredytowania w latach<Input aria-label="Okres kredytowania w latach" name="years" value={values.years} onChange={update} inputMode="numeric" $invalid={errors.years} /><Hint>Możesz wpisać dowolny okres, np. 27, 30 albo 32 lata.</Hint>{errors.years && <Error>{errors.years}</Error>}</Field>
            <FieldGrid>
              <Field>Rodzaj rat<Select aria-label="Rodzaj rat" name="repaymentType" value={values.repaymentType} onChange={update}><option value="equal">Raty równe</option><option value="decreasing">Raty malejące</option></Select></Field>
              <Field>Rodzaj oprocentowania<Select aria-label="Rodzaj oprocentowania" name="rateType" value={values.rateType} onChange={update}><option value="variable">Zmienna stopa</option><option value="fixed">Okresowo stała stopa</option></Select></Field>
            </FieldGrid>
            <Field>Jak porównać warianty?<Select aria-label="Sposób porównania wariantów" name="calculationMode" value={values.calculationMode} onChange={update}><option value="realistic">Realistycznie: osobne oprocentowanie</option><option value="single">Prosto: jedno oprocentowanie</option></Select><Hint>Osobne wartości lepiej pokazują, że bank może inaczej wyceniać 10%, 20% i RKM.</Hint></Field>
            {values.calculationMode === "single" ? (
              <Field>Jedno oprocentowanie do symulacji (%)<Input aria-label="Jedno oprocentowanie do symulacji" name="singleRate" value={values.singleRate} onChange={update} inputMode="decimal" $invalid={errors.singleRate} />{errors.singleRate && <Error>{errors.singleRate}</Error>}</Field>
            ) : (
              <FieldGrid>
                <Field>Oprocentowanie 10%<Input aria-label="Oprocentowanie dla wkładu 10%" name="rate10" value={values.rate10} onChange={update} inputMode="decimal" $invalid={errors.rate10} />{errors.rate10 && <Error>{errors.rate10}</Error>}</Field>
                <Field>Oprocentowanie 20%<Input aria-label="Oprocentowanie dla wkładu 20%" name="rate20" value={values.rate20} onChange={update} inputMode="decimal" $invalid={errors.rate20} />{errors.rate20 && <Error>{errors.rate20}</Error>}</Field>
                <Field>Oprocentowanie RKM<Input aria-label="Oprocentowanie RKM" name="rateRkm" value={values.rateRkm} onChange={update} inputMode="decimal" $invalid={errors.rateRkm} />{errors.rateRkm && <Error>{errors.rateRkm}</Error>}</Field>
              </FieldGrid>
            )}
            <Hint>Wpisz łączne oprocentowanie nominalne z konkretnej oferty. Nie wpisuj samej marży ani RRSO.</Hint>
            <Hint>Wartości początkowe są przykładowe. Przed porównaniem zastąp je aktualnymi parametrami ofert.</Hint>
            <Submit type="submit">Policz warianty</Submit>
          </Form>
        </Panel>

        <Panel id="wynik-raty" aria-live="polite">
          <ResultHeading><h2>{isDirty ? "Wynik dla poprzednich danych" : "Twój wynik"}</h2><p>Raty {input.repaymentType === "decreasing" ? "malejące" : "równe"} · {input.years} lat</p></ResultHeading>
          {isDirty && <Error>Dane zostały zmienione. Kliknij „Policz warianty”, aby odświeżyć wynik.</Error>}
          <Insight><strong>Jak czytać wynik?</strong>Przy osobnych założeniach oprocentowania wynik pokazuje różnice między wariantami. Kolejność kart nie jest rekomendacją banku.</Insight>
          <ScenarioGrid>{visibleItems.map((item) => <ScenarioCard key={item.key} item={item} best={result.recommended} repaymentType={input.repaymentType} />)}</ScenarioGrid>
          <Assumptions><strong>Ważne:</strong> RKM jest pokazany orientacyjnie. BGK może zabezpieczyć maksymalnie 100 000 zł, a wkład własny w programie nie może przekroczyć 200 000 zł oraz 20% ceny przy stopie zmiennej albo 30% przy okresowo stałej. Program obejmuje również warunki dotyczące gospodarstwa domowego, posiadanych nieruchomości i limitu ceny za m². Kalkulator zakłada wkład pieniężny. Działka lub Premia Mieszkaniowa wymagają osobnej analizy. Bank oddzielnie ocenia zdolność i spełnienie wszystkich warunków programu.</Assumptions>
          <Funnel>
            <Eyebrow>Następny krok</Eyebrow>
            <h3>Wynik to początek — sprawdźmy, co jest realne dla Ciebie</h3>
            <p>Podczas rozmowy przełożymy symulację na aktualne oferty, zdolność, koszty i bezpieczny poziom raty. Rozmowa oraz obsługa kredytowa nie wiążą się dla klienta z dodatkową opłatą. Wynagrodzenie otrzymujemy od banku po uruchomieniu kredytu.</p>
            <Actions><CTA to="/?source=kalkulator-rata-wklad" data-contact-cta>Umów rozmowę o wyniku</CTA><Secondary type="button" onClick={copySummary}>Skopiuj podsumowanie</Secondary></Actions>
            {copied && <Hint>Skopiowano. Możesz wkleić podsumowanie w formularzu kontaktowym.</Hint>}
          </Funnel>
        </Panel>
      </Layout>

      <Disclaimer><h2>Założenia symulacji</h2><p>Raty liczone są przy niezmiennym oprocentowaniu wpisanym w formularzu. Kalkulator nie uwzględnia prowizji bankowej, ubezpieczeń, produktów dodatkowych, kosztów zakupu ani zmian wskaźnika referencyjnego. W wariancie RKM pokazuje osobno prowizję BGK wynoszącą 1% gwarancji. Rzeczywista rata, marża i zdolność kredytowa zależą od banku oraz indywidualnej sytuacji klienta.</p></Disclaimer>
    </Page>
  );
};

export default KalkulatorRataWklad;

